import * as Sentry from '@sentry/react'

const { VITE_SENTRY_ENVIRONMENT: SENTRY_ENVIRONMENT } = import.meta.env
const LOCAL_SENTRY_ENVIRONMENTS = new Set(['development', 'local'])
const LOCALHOST_HOSTNAMES = new Set(['localhost', '127.0.0.1', '::1'])

const PACKAGE_NAME = process.env.npm_package_name
const PACKAGE_VERSION = process.env.npm_package_version
const COMMIT_HASH = process.env.RENDER_GIT_COMMIT

interface SentryTransactionEvent {
  request?: {
    url?: string
  }
}

const normalizeHostname = (hostname: string): string => hostname.replace(/^\[(.*)\]$/, '$1')

const hasLocalhostUrl = (url: string | undefined): boolean => {
  if (url == null) {
    return false
  }

  try {
    return LOCALHOST_HOSTNAMES.has(normalizeHostname(new URL(url).hostname))
  } catch {
    return false
  }
}

export const isLocalPerformanceTransaction = (
  event: SentryTransactionEvent,
  configuredEnvironment?: string,
): boolean => {
  if (!LOCAL_SENTRY_ENVIRONMENTS.has(configuredEnvironment ?? '')) {
    return false
  }

  return hasLocalhostUrl(event.request?.url)
}

export const isLocalhostSentryEvent = (event: Sentry.Event): boolean => hasLocalhostUrl(event.request?.url)

export const setupSentry = (): void => {
  Sentry.init({
    dsn: 'https://d16d3d25ac134d689de5f495aa03c372@o793342.ingest.sentry.io/5801370',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    environment: SENTRY_ENVIRONMENT,
    release: `${PACKAGE_NAME}@${PACKAGE_VERSION}:${COMMIT_HASH}`,
    beforeSend: (event) => (isLocalhostSentryEvent(event) ? null : event),

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,
    beforeSendTransaction: (event) => (isLocalPerformanceTransaction(event, SENTRY_ENVIRONMENT) ? null : event),

    // Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  })
}
